import styled, { css } from 'styled-components'

export const BreadcrumbContainer = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const BreadcrumbList = styled.ol`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
  list-style: none;
  margin: 0;
  padding: 0;
`

export const BreadcrumbListItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};

  /* Hide middle items on mobile when collapsed */
  &.collapsed-item {
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
      display: none;
    }
  }
`

const itemBaseStyles = css`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  text-decoration: none;
  white-space: nowrap;
  transition: all ${({ theme }) => theme.transitions.fast};

  /* Mobile: Ensure 44px minimum touch target for clickable items */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-height: ${({ theme }) => theme.touchTargets.minimum};
    padding: ${({ theme }) => theme.spacing.small};
  }
`

export const BreadcrumbLink = styled.a<{ $active?: boolean }>`
  ${itemBaseStyles}
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};

  &:hover {
    color: ${({ $active, theme }) =>
      $active ? theme.colors.text.primary : theme.colors.primary};
    background: ${({ $active, theme }) =>
      $active ? 'transparent' : theme.colors.background.secondary};
  }

  /* Touch devices: Disable hover effects */
  @media (hover: none) {
    &:hover {
      color: ${({ $active, theme }) =>
        $active ? theme.colors.text.primary : theme.colors.text.secondary};
      background: transparent;
    }

    &:active {
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.background.secondary};
    }
  }
`

export const BreadcrumbText = styled.span<{ $active?: boolean }>`
  ${itemBaseStyles}
  color: ${({ $active, theme }) =>
    $active ? theme.colors.text.primary : theme.colors.text.secondary};
  font-weight: ${({ $active, theme }) =>
    $active
      ? theme.typography.fontWeight.medium
      : theme.typography.fontWeight.normal};
`

export const Separator = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  display: flex;
  align-items: center;
  user-select: none;

  svg {
    width: 16px;
    height: 16px;
  }
`

export const EllipsisButton = styled.button`
  ${itemBaseStyles}
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  /* Mobile: Show ellipsis button only on mobile */
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: inline-flex;
    min-width: ${({ theme }) => theme.touchTargets.minimum};
    min-height: ${({ theme }) => theme.touchTargets.minimum};
    justify-content: center;
  }

  /* Touch devices: Disable hover effects */
  @media (hover: none) {
    &:hover {
      color: ${({ theme }) => theme.colors.text.secondary};
      background: transparent;
    }

    &:active {
      color: ${({ theme }) => theme.colors.primary};
      background: ${({ theme }) => theme.colors.background.secondary};
    }
  }
`

export const IconWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
`
